import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileText, X } from "lucide-react";

const empty = { product: "All", name: "", mandatory: true };

export default function MasterDocuments() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get("/doc-master").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active).map((p) => p.name))).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/doc-master/${editId}`, form);
      else await api.post("/doc-master", form);
      toast.success(editId ? "Document updated" : "Document added");
      setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const edit = (r) => { setForm({ product: r.product, name: r.name, mandatory: r.mandatory }); setEditId(r.id); };
  const del = async (id) => { if (!window.confirm("Remove this document?")) return; await api.delete(`/doc-master/${id}`); toast.success("Removed"); load(); };

  const grouped = rows.reduce((acc, r) => { (acc[r.product] = acc[r.product] || []).push(r); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="master-documents-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><FileText size={24} className="text-emerald-600" /> Master Documents</h1>
        <p className="text-sm text-slate-500 mt-1">Define the documents required per product and mark which are mandatory</p>
      </div>

      <form onSubmit={save} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
        <div>
          <label className={labelCls}>Product</label>
          <select data-testid="docm-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className={inputCls}>
            <option value="All">All Products</option>
            {products.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><label className={labelCls}>Document Name *</label><input data-testid="docm-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. Latest 6-month bank statement" /></div>
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="docm-mandatory" type="checkbox" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Mandatory</label>
          <div className="flex gap-2">
            {editId && <button type="button" onClick={() => { setForm(empty); setEditId(null); }} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>}
            <button data-testid="docm-submit" type="submit" className={btnPrimary}><Plus size={16} /> {editId ? "Update" : "Add"}</button>
          </div>
        </div>
      </form>

      {Object.keys(grouped).length === 0 && <p className="text-sm text-slate-400">No master documents defined yet.</p>}
      {Object.entries(grouped).map(([prod, items]) => (
        <div key={prod} className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">{prod}</div>
          <table className="w-full">
            <tbody>
              {items.map((r) => (
                <tr key={r.id} data-testid={`docm-row-${r.id}`} className="border-b border-slate-100 text-sm text-slate-700">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-3">{r.mandatory ? <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 font-medium">Mandatory</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Optional</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`docm-edit-${r.id}`} onClick={() => edit(r)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>
                    <button data-testid={`docm-del-${r.id}`} onClick={() => del(r.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
